import {useEffect, useMemo, useState} from 'react'
import {Link} from 'react-router-dom'
import Chart from 'react-apexcharts'
import {BASE_URL} from '../../../api'
import config from '../../config'
const {axios} = config

// Practitioner analytics (2026-10-13). How practitioners are seen and
// tapped (anonymous events from the booking website, Showit pages and - from
// the next app release - the app), joined with bookings, booking value,
// EnForma fees, reviews and messages from the database. Backend:
// GET /analytics/summary (enforma-backend/helpers/analyticsReport.helper.js).

const TRACKING_STARTED = '2026-10-13'
const LINE_COLOR = '#3F7F4C' // sage, darkened to read on white

const day = (d) => d.toISOString().slice(0, 10)
const daysAgo = (n) => day(new Date(Date.now() - n * 864e5))
const RANGES = {
  '7': {label: 'Last 7 days', from: () => daysAgo(6), to: () => daysAgo(0)},
  '30': {label: 'Last 30 days', from: () => daysAgo(29), to: () => daysAgo(0)},
  '90': {label: 'Last 90 days', from: () => daysAgo(89), to: () => daysAgo(0)},
  month: {
    label: 'This month',
    from: () => daysAgo(0).slice(0, 8) + '01',
    to: () => daysAgo(0),
  },
  lastMonth: {
    label: 'Last month',
    from: () => {
      const d = new Date()
      return day(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1)))
    },
    to: () => {
      const d = new Date()
      return day(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 0)))
    },
  },
}
const CHANNELS = {'': 'Everywhere', web: 'Booking website', showit: 'Showit pages', app: 'App'}
const REFERRERS = {
  showit: 'From a Showit page',
  search: 'Google / search',
  social: 'Instagram / social',
  push: 'Push notification',
  email: 'Email',
  direct: 'Direct / typed link',
  internal: 'Browsing the site',
  qr: 'QR code',
  other: 'Other websites',
}

const n = (v) => (v || 0).toLocaleString('en-US')
const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : '–')
const money = (cents) => {
  const parts = Object.entries(cents || {})
    .filter(([, v]) => v)
    .map(([cur, v]) => `${cur} ${(v / 100).toLocaleString('en-US', {maximumFractionDigits: 0})}`)
  return parts.length ? parts.join(' · ') : '–'
}
// Location values the app stores on profiles -> name and Showit subdomain.
const TOWNS = {
  Sayulita_Nay: {label: 'Sayulita', host: 'sayulita.'},
  San_Miguel_De_Allende: {label: 'San Miguel de Allende', host: 'sanmigueldeallende.'},
  'Puerto Escondido': {label: 'Puerto Escondido', host: 'puertoescondido.'},
  Tulum: {label: 'Tulum', host: 'tulum.'},
}
const townLabel = (v) => TOWNS[v]?.label || v
const townHost = (v) => TOWNS[v]?.host || '//none/'
const abandoned = (p) => Math.max(0, (p.counts.checkout_start || 0) - (p.bookings || 0))

const COLUMNS = [
  {key: 'impression', label: 'Appearances', get: (p) => p.counts.impression, hint: 'Shown in a listing'},
  {key: 'profile_view', label: 'Profile views', get: (p) => p.counts.profile_view},
  {key: 'tap_book', label: 'Book taps', get: (p) => p.counts.tap_book},
  {key: 'tap_message', label: 'Message', get: (p) => p.counts.tap_message},
  {key: 'tap_whatsapp', label: 'WhatsApp', get: (p) => p.counts.tap_whatsapp},
  {key: 'tap_instagram', label: 'Instagram', get: (p) => p.counts.tap_instagram},
  {key: 'tap_website', label: 'Website', get: (p) => p.counts.tap_website},
  {key: 'abandoned', label: 'Not finished', get: abandoned, hint: 'Reached payment but didn’t book'},
  {key: 'bookings', label: 'Bookings', get: (p) => p.bookings},
  {key: 'value', label: 'Booking value', get: (p) => Object.values(p.valueCents).reduce((s, v) => s + v, 0), show: (p) => money(p.valueCents)},
  {key: 'fee', label: 'EnForma fees', get: (p) => Object.values(p.feeCents).reduce((s, v) => s + v, 0), show: (p) => money(p.feeCents)},
  {key: 'reviews', label: 'Reviews', get: (p) => p.reviews, show: (p) => (p.reviews ? `${p.reviews} (★ ${p.ratingInRange})` : '–')},
  {key: 'messages', label: 'Messages in', get: (p) => p.messages, show: (p) => (p.messages ? `${n(p.messages)} from ${n(p.messagers)}` : '–')},
]

const csvCell = (v) => {
  const s = String(v ?? '')
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function downloadCsv(rows, from, to) {
  const currencies = [...new Set(rows.flatMap((p) => [...Object.keys(p.valueCents), ...Object.keys(p.feeCents)]))].sort()
  const header = [
    'Practitioner', 'Town', 'Member', 'Appearances', 'Profile views', 'Book taps', 'Message taps', 'WhatsApp taps',
    'Instagram taps', 'Website taps', 'Payment started', 'Bookings', 'Service bookings', 'Class bookings',
    ...currencies.map((c) => `Booking value ${c}`), ...currencies.map((c) => `EnForma fees ${c}`),
    'Reviews', 'Avg rating (period)', 'Messages received', 'People who messaged', 'Practitioner id',
  ]
  const lines = rows.map((p) => [
    p.name, townLabel(p.town), p.member ? 'Yes' : 'Free listing', p.counts.impression, p.counts.profile_view, p.counts.tap_book,
    p.counts.tap_message, p.counts.tap_whatsapp, p.counts.tap_instagram, p.counts.tap_website, p.counts.checkout_start,
    p.bookings, p.serviceBookings, p.classBookings,
    ...currencies.map((c) => ((p.valueCents[c] || 0) / 100).toFixed(2)),
    ...currencies.map((c) => ((p.feeCents[c] || 0) / 100).toFixed(2)),
    p.reviews, p.ratingInRange ?? '', p.messages, p.messagers, p._id,
  ])
  const csv = [header, ...lines].map((l) => l.map(csvCell).join(',')).join('\n')
  const url = URL.createObjectURL(new Blob(['﻿' + csv], {type: 'text/csv;charset=utf-8'}))
  const a = document.createElement('a')
  a.href = url
  a.download = `enforma-practitioner-analytics-${from}-to-${to}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

const Tile = ({label, value, sub, small}) => (
  <div className='col-6 col-md-4 col-xl'>
    <div className='border rounded p-4 h-100'>
      <div className='text-muted fs-7 mb-1'>{label}</div>
      <div className={`${small ? 'fs-4' : 'fs-2'} fw-bolder`}>{value}</div>
      {sub && <div className='text-muted fs-8 mt-1'>{sub}</div>}
    </div>
  </div>
)

const PractitionerAnalytics = () => {
  const [range, setRange] = useState('30')
  const [channel, setChannel] = useState('')
  const [town, setTown] = useState('')
  const [members, setMembers] = useState('')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState({key: 'bookings', dir: -1})
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const from = RANGES[range].from()
  const to = RANGES[range].to()

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError('')
    axios
      .get(`${BASE_URL}/analytics/summary`, {params: {from, to, channel: channel || undefined}})
      .then(({data}) => alive && setData(data))
      .catch(() => alive && setError('Could not load analytics.'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [from, to, channel])

  const towns = useMemo(
    () => [...new Set((data?.practitioners || []).map((p) => p.town).filter(Boolean))].sort(),
    [data]
  )

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    const col = COLUMNS.find((c) => c.key === sort.key)
    return (data?.practitioners || [])
      .filter((p) => !town || p.town === town)
      .filter((p) => !members || (members === 'member' ? p.member : !p.member))
      .filter((p) => !q || p.name.toLowerCase().includes(q))
      .sort((a, b) => sort.dir * ((col ? col.get(a) - col.get(b) : 0) || a.name.localeCompare(b.name)))
  }, [data, town, members, search, sort])

  // Tiles add up the rows on screen, so the town / member filters apply.
  const t = useMemo(() => {
    const sum = (f) => rows.reduce((s, p) => s + (f(p) || 0), 0)
    const addCur = (key) =>
      rows.reduce((acc, p) => {
        Object.entries(p[key]).forEach(([c, v]) => (acc[c] = (acc[c] || 0) + v))
        return acc
      }, {})
    return {
      impression: sum((p) => p.counts.impression),
      profile_view: sum((p) => p.counts.profile_view),
      tap_book: sum((p) => p.counts.tap_book),
      checkout_start: sum((p) => p.counts.checkout_start),
      bookings: sum((p) => p.bookings),
      value: addCur('valueCents'),
      fee: addCur('feeCents'),
    }
  }, [rows])

  const sourceTotal = (data?.profileViewSources || []).reduce((s, x) => s + x.count, 0)
  const unlinked = (data?.unlinkedPages || []).filter((pg) => !town || pg.page.includes(townHost(town)))

  const sortBy = (key) => setSort((s) => ({key, dir: s.key === key ? -s.dir : -1}))
  const filtered = town || members || search

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-6 flex-wrap gap-3'>
        <div className='card-title m-0 flex-column align-items-start'>
          <h3 className='fw-bolder m-0'>Practitioner Analytics</h3>
          <span className='text-muted fs-7 mt-1'>
            {data ? `${data.from} to ${data.to}` : ' '}
          </span>
        </div>
        <div className='card-toolbar gap-2 flex-wrap'>
          <select className='form-select form-select-solid w-150px' value={range} onChange={(e) => setRange(e.target.value)}>
            {Object.entries(RANGES).map(([k, r]) => (
              <option key={k} value={k}>{r.label}</option>
            ))}
          </select>
          <select className='form-select form-select-solid w-175px' value={channel} onChange={(e) => setChannel(e.target.value)} title='Where views and taps happened'>
            {Object.entries(CHANNELS).map(([k, l]) => (
              <option key={k} value={k}>{l}</option>
            ))}
          </select>
          <select className='form-select form-select-solid w-175px' value={town} onChange={(e) => setTown(e.target.value)}>
            <option value=''>All towns</option>
            {towns.map((x) => (
              <option key={x} value={x}>{townLabel(x)}</option>
            ))}
          </select>
          <select className='form-select form-select-solid w-150px' value={members} onChange={(e) => setMembers(e.target.value)}>
            <option value=''>Members + free</option>
            <option value='member'>Members only</option>
            <option value='free'>Free listings only</option>
          </select>
          <button className='btn btn-light-primary' disabled={!rows.length} onClick={() => downloadCsv(rows, data.from, data.to)}>
            Export CSV
          </button>
        </div>
      </div>

      <div className='card-body pt-2'>
        <div className='alert alert-secondary py-3 fs-7'>
          Views and taps are counted from {TRACKING_STARTED} on the booking website and Showit pages. App views and
          taps start with the next app release. Bookings count those still standing (cancelled bookings are deleted, so
          they don't show). Counting is anonymous: no client names, emails or devices are stored.
        </div>
        {error && <div className='alert alert-danger py-3'>{error}</div>}
        {loading && !data && <div className='text-muted py-10 text-center'>Loading…</div>}

        {data && (
          <div style={{opacity: loading ? 0.5 : 1}}>
            {filtered && <div className='text-muted fs-7 mb-2'>Totals below are for the filtered list ({rows.length} practitioners).</div>}
            <div className='row g-3 mb-8'>
              <Tile label='Listing appearances' value={n(t.impression)} />
              <Tile label='Profile views' value={n(t.profile_view)} sub={`${pct(t.profile_view, t.impression)} of appearances`} />
              <Tile label='Book taps' value={n(t.tap_book)} sub={`${pct(t.tap_book, t.profile_view)} of profile views`} />
              <Tile label='Reached payment' value={n(t.checkout_start)} sub={`${pct(t.checkout_start, t.tap_book)} of Book taps`} />
              <Tile label='Bookings' value={n(t.bookings)} />
              <Tile small label='Booking value' value={money(t.value)} sub={`EnForma fees ${money(t.fee)}`} />
            </div>

            <div className='row g-8 mb-8'>
              <div className='col-xl-8'>
                <h5 className='fw-bold mb-1'>Profile views per day</h5>
                <div className='text-muted fs-8 mb-2'>All practitioners{channel ? ` · ${CHANNELS[channel]}` : ''}</div>
                <Chart
                  type='area'
                  height={240}
                  series={[{name: 'Profile views', data: data.daily.map((d) => ({x: d.day, y: d.profile_view}))}]}
                  options={{
                    chart: {toolbar: {show: false}, zoom: {enabled: false}, fontFamily: 'inherit'},
                    colors: [LINE_COLOR],
                    stroke: {width: 2, curve: 'straight'},
                    fill: {type: 'solid', opacity: 0.12},
                    dataLabels: {enabled: false},
                    markers: {size: 0, hover: {size: 5}},
                    grid: {borderColor: '#eee', strokeDashArray: 3},
                    xaxis: {type: 'datetime', labels: {datetimeUTC: true, style: {colors: '#888'}}, axisBorder: {show: false}},
                    yaxis: {min: 0, forceNiceScale: true, labels: {formatter: (v) => Math.round(v), style: {colors: '#888'}}},
                    tooltip: {x: {format: 'ddd d MMM'}},
                  }}
                />
              </div>
              <div className='col-xl-4'>
                <h5 className='fw-bold mb-1'>Where profile views came from</h5>
                <div className='text-muted fs-8 mb-2'>Where the view happened · how the visitor arrived</div>
                {!data.profileViewSources.length ? (
                  <div className='text-muted fs-7'>No profile views yet in this period.</div>
                ) : (
                  <table className='table table-row-dashed fs-7 gy-2 mb-0'>
                    <tbody>
                      {data.profileViewSources.map((s) => (
                        <tr key={`${s.channel}-${s.referrer}`}>
                          <td>
                            {CHANNELS[s.channel] || s.channel}
                            <div className='text-muted fs-8'>{REFERRERS[s.referrer] || s.referrer}</div>
                          </td>
                          <td className='text-end fw-bold'>{n(s.count)}</td>
                          <td className='text-end text-muted w-50px'>{pct(s.count, sourceTotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {!!data.totals.counts.push_open && (
                  <div className='mt-4 fs-7'>
                    Push notifications opened: <b>{n(data.totals.counts.push_open)}</b>
                  </div>
                )}
              </div>
            </div>

            <div className='d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2'>
              <h5 className='fw-bold m-0'>Leaderboard</h5>
              <input
                className='form-control form-control-solid w-250px'
                placeholder='Search practitioner'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className='table-responsive'>
              <table className='table table-row-dashed align-middle fs-7 gy-3'>
                <thead>
                  <tr className='text-muted fw-bold text-uppercase fs-8'>
                    <th className='min-w-200px'>Practitioner</th>
                    {COLUMNS.map((c) => (
                      <th
                        key={c.key}
                        className='text-end text-nowrap'
                        role='button'
                        title={c.hint || `Sort by ${c.label}`}
                        onClick={() => sortBy(c.key)}
                      >
                        {c.label}
                        {sort.key === c.key ? (sort.dir < 0 ? ' ↓' : ' ↑') : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p._id}>
                      <td>
                        <div className='d-flex align-items-center gap-3'>
                          <div
                            className='rounded-circle bg-light flex-shrink-0'
                            style={{
                              width: 36,
                              height: 36,
                              backgroundImage: p.profileImageUrl ? `url(${p.profileImageUrl})` : undefined,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }}
                          />
                          <div>
                            <Link to={`/apps/agents/details-page/${p._id}/bookings`} className='text-dark fw-bold text-hover-primary'>
                              {p.name || '(no name)'}
                            </Link>
                            <div className='text-muted fs-8'>
                              {townLabel(p.town) || '—'} ·{' '}
                              <span className={p.member ? 'text-success' : ''}>{p.member ? 'Member' : 'Free listing'}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      {COLUMNS.map((c) => (
                        <td key={c.key} className='text-end text-nowrap'>
                          {c.show ? c.show(p) : c.get(p) ? n(c.get(p)) : '–'}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {!rows.length && (
                    <tr>
                      <td colSpan={COLUMNS.length + 1} className='text-center text-muted py-10'>
                        No activity in this period yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {!!unlinked.length && !members && !search && (
              <div className='mt-10'>
                <h5 className='fw-bold mb-1'>Showit pages not linked to a profile</h5>
                <div className='text-muted fs-8 mb-3'>
                  Views on Showit pages that no practitioner or business in the app is linked to, e.g. retreats
                  without an app profile, plus general pages like About. To move a page into the leaderboard, open
                  the profile in Users management and paste the page address into Website page. Its past views move
                  with it.
                </div>
                <div className='table-responsive'>
                  <table className='table table-row-dashed align-middle fs-7 gy-2'>
                    <thead>
                      <tr className='text-muted fw-bold text-uppercase fs-8'>
                        <th>Page</th>
                        <th className='text-end'>Views</th>
                        <th className='text-end'>Instagram</th>
                        <th className='text-end'>WhatsApp</th>
                        <th className='text-end'>Website</th>
                        <th className='text-end'>Book taps</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unlinked.map((pg) => (
                        <tr key={pg.page}>
                          <td>
                            <a href={pg.page} target='_blank' rel='noreferrer'>
                              {pg.page.replace(/^https:\/\//, '')}
                            </a>
                          </td>
                          {['profile_view', 'tap_instagram', 'tap_whatsapp', 'tap_website', 'tap_book'].map((k) => (
                            <td key={k} className='text-end'>{pg.counts[k] ? n(pg.counts[k]) : '–'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default PractitionerAnalytics
